import { useEffect, useState, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, Loader2, XCircle, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import { trackPurchase } from '@/utils/analytics';

const CheckoutSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('polling');
  const [bookingId, setBookingId] = useState(null);
  const [crawlId, setCrawlId] = useState(null);
  const pollCount = useRef(0);
  const pollDone = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }
    pollPaymentStatus();
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    if (pollDone.current) return;
    const maxAttempts = 8;
    const interval = 2500;

    if (pollCount.current >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const res = await axios.get(`${API}/checkout/status/${sessionId}`);
      const data = res.data;

      if (data.payment_status === 'paid') {
        pollDone.current = true;
        setBookingId(data.booking_id);
        setCrawlId(data.crawl_id);
        setStatus('success');
        trackPurchase(data.booking_id, data.crawl_id, 0);
        return;
      } else if (data.status === 'expired') {
        pollDone.current = true;
        setStatus('expired');
        return;
      }

      pollCount.current += 1;
      setTimeout(pollPaymentStatus, interval);
    } catch {
      pollCount.current += 1;
      setTimeout(pollPaymentStatus, interval);
    }
  };

  return (
    <div className="min-h-screen bg-deep-black pt-24 px-4 flex items-center justify-center" data-testid="checkout-success-page">
      <div className="max-w-lg w-full text-center">
        {status === 'polling' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <Loader2 size={48} className="text-hot-pink animate-spin mx-auto" />
            <h1 className="font-display text-[40px] text-warm-white uppercase">Processing Payment</h1>
            <p className="font-body text-warm-white/60">Hang tight — confirming your booking...</p>
          </motion.div>
        )}

        {status === 'success' && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="space-y-6"
          >
            <div className="w-20 h-20 bg-green-500/20 mx-auto flex items-center justify-center">
              <Check size={40} className="text-green-400" />
            </div>
            <h1 className="font-display text-[48px] text-warm-white uppercase" data-testid="checkout-success-title">
              You're In!
            </h1>
            <p className="font-body text-warm-white/70 text-lg">
              Your booking is confirmed. Check your email for details and your wristband info.
            </p>
            {bookingId && (
              <p className="font-mono text-[11px] text-warm-white/40 uppercase tracking-wide">
                Booking ID: {bookingId.slice(0, 8)}
              </p>
            )}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
              {crawlId && (
                <Link to={`/crawls/${crawlId}`}>
                  <button className="btn-secondary px-8 py-3">View Crawl Details</button>
                </Link>
              )}
              <Link to="/crawls">
                <button className="btn-primary px-8 py-3 inline-flex items-center gap-2" data-testid="browse-more-btn">
                  Browse More Crawls <ArrowRight size={16} />
                </button>
              </Link>
            </div>
          </motion.div>
        )}

        {(status === 'error' || status === 'expired') && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <XCircle size={48} className="text-red-400 mx-auto" />
            <h1 className="font-display text-[40px] text-warm-white uppercase">
              {status === 'expired' ? 'Session Expired' : 'Something Went Wrong'}
            </h1>
            <p className="font-body text-warm-white/60">
              {status === 'expired'
                ? 'Your payment session has expired. Please try booking again.'
                : 'We couldn\'t verify your payment. If you were charged, contact us.'}
            </p>
            <Link to="/crawls">
              <button className="btn-primary px-8 py-3 mt-4">Back to Crawls</button>
            </Link>
          </motion.div>
        )}

        {status === 'timeout' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <Loader2 size={48} className="text-yellow-400 mx-auto" />
            <h1 className="font-display text-[40px] text-warm-white uppercase">Still Processing</h1>
            <p className="font-body text-warm-white/60">
              Payment is taking longer than expected. Check your email for confirmation or contact us.
            </p>
            <Link to="/contact">
              <button className="btn-primary px-8 py-3 mt-4">Contact Us</button>
            </Link>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default CheckoutSuccessPage;
